import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  LeadPipelineTemplateActions,
  PipelineActionTemplateNameMap,
} from 'components/domain/template/modal/TemplateModal.types';
import useOpenTemplateModal from 'components/domain/template/modal/useOpenTemplateModal';
import { PopoverProps } from 'components/popover/PopoverStickyOnHover';
import Tooltip from 'components/tooltip/Tooltip';
import useAppEvent from 'hooks/useAppEvent';
import useAppModal from 'hooks/useAppModal';
import { AppEventType } from 'models/Events';
import { Lead } from 'models/Leads';
import LeadActionsPopover from './LeadActionsPopover';
import useLeadActionsPermission from './useLeadActionsPermission';

interface LeadActionsPopoverWithDefaultActionsProps {
  baseId?: string;
  children: ReactNode;
  containerSelector?: string;
  lead: Lead;
  placement?: PopoverProps['placement'];
}

const LeadActionsPopoverWithDefaultActions = ({
  baseId,
  children,
  containerSelector,
  lead,
  placement,
}: LeadActionsPopoverWithDefaultActionsProps) => {
  const { t } = useTranslation();
  const { publish } = useAppEvent();
  const { closeModal } = useAppModal();
  const { createTemplatedModal, templateModalId } = useOpenTemplateModal();
  const canExecutePipelineActions = useLeadActionsPermission(lead);

  const { availablePipelineActions, uid } = lead;

  if (!canExecutePipelineActions && availablePipelineActions?.length) {
    return (
      <Tooltip
        id={`no-actions-permission-tooltip-${uid}`}
        placement="left"
        text={t('componentLead.actionsInfo.noPermission')}
      >
        {children}
      </Tooltip>
    );
  }

  const updateLeadCallback = () => {
    publish(AppEventType.LEAD_UPDATED, { lead: { uid } });
  };

  // @ts-expect-error TS7031 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  const messagingActionCallback = ({ action, lead: _lead }) => {
    if (
      PipelineActionTemplateNameMap.get(action) ||
      LeadPipelineTemplateActions.includes(action)
    ) {
      createTemplatedModal({
        templateName: PipelineActionTemplateNameMap.get(action),
        leadUid: lead.uid,
        leadPipelineTemplateAction: action,
        updateLeadCallback: () => {
          closeModal(templateModalId);
          updateLeadCallback();
        },
      });
    } else {
      // oxlint-disable-next-line no-alert
      alert(`TODO implement lead messaging action: ${action}`);
    }
  };

  return (
    <LeadActionsPopover
      baseId={baseId}
      containerSelector={containerSelector}
      lead={lead}
      messagingActionCallback={messagingActionCallback}
      placement={placement}
      updateLeadCallback={updateLeadCallback}
    >
      {children}
    </LeadActionsPopover>
  );
};

LeadActionsPopoverWithDefaultActions.defaultProps = {
  baseId: undefined,
  containerSelector: undefined,
  placement: undefined,
};

export default LeadActionsPopoverWithDefaultActions;
